import type {
  CockpitArtifactAccess,
  CockpitArtifactRole,
  CockpitConfidence,
  CockpitCoverage,
  CockpitDeliveryProjection,
  CockpitGuaranteeResult,
  CockpitLifecycle,
  CockpitProvenance,
  CockpitRelation,
  CockpitSectionStatus,
  CockpitUsageCostReason,
  CockpitUsageObservationState,
  SessionEvidenceState,
  SessionIntegrationState,
  SessionObservabilityActionCode,
  SessionObservabilityCapability,
  SessionObservabilityReasonCode,
  UsageExclusionReason,
  UsageReason,
} from '@internal/spassu-code-contract';

export type CockpitNodeKind = 'skill' | 'artifact' | 'control' | 'timeline';

export interface CockpitViewNode {
  id: string;
  kind: CockpitNodeKind;
  label: string;
  confidence: CockpitConfidence;
  lifecycle: CockpitLifecycle | 'available' | 'deleted';
  coverage: CockpitCoverage;
  critical?: boolean;
}

export interface CockpitSkillView extends CockpitViewNode {
  kind: 'skill';
  version: string | null;
  sourceLabel: string;
  attempt: number;
  durationMs: number | null;
  evidenceIds: string[];
  artifactIds: string[];
  limitations: string[];
}

export interface CockpitArtifactView extends CockpitViewNode {
  kind: 'artifact';
  artifactType: string | null;
  phase: string | null;
  path: string | null;
  skillName: string | null;
  role: CockpitArtifactRole;
  access: CockpitArtifactAccess;
  version: string | null;
  revisionKey: string;
  provenance: CockpitProvenance;
  createdAt: string | null;
  invocationId: string | null;
  evidenceIds: string[];
  limitations: string[];
  content?: string | null;
}

export interface CockpitSectionViewState {
  status: CockpitSectionStatus;
  coverage: CockpitCoverage;
  limitations: string[];
}

export interface CockpitHeaderView {
  title: string;
  viaLabel: string;
  statusLabel: string;
  evidenceLabel: string;
  /** Runtime reported by the observed sessions; `null` renders no chip (RN-COCK-07). */
  runtimeLabel: string | null;
  updatedAt: string;
  historical: boolean;
  revision: number | null;
}

export type CockpitRailState =
  | 'evidence'
  | 'running'
  | 'detected'
  | 'inferred'
  | 'covered'
  | 'wait'
  | 'void';

export interface CockpitRailSegment {
  id: string;
  nodeId: string | null;
  label: string;
  state: CockpitRailState;
  durationMs: number;
  startedAt: string;
  endedAt: string;
}

/** One parcel of the input-token breakdown. A source that never reported the
 * parcel keeps `value: null`, rendered as "não informada" — never a zero. */
export interface CockpitMetricParcelView {
  label: string;
  value: number | null;
  displayValue: string;
}

/** One row of the per-model / per-phase / per-skill breakdown table. */
export interface CockpitMetricBreakdownRow {
  key: string;
  label: string;
  usages: number;
  inputTokens: number;
  outputTokens: number;
  costsLabel: string | null;
}

/** Totals of records written before billed semantics landed (ADR-006); kept
 * out of the primary total and surfaced as a separate, explicit notice. */
export interface CockpitMetricLegacyNoticeView {
  usages: number;
  inputTokens: number;
  outputTokens: number;
}

/**
 * One row of the "Turnos" table (US-012): a count of user/agent turns, never
 * their text (BR/AC-2 of US-012). `key` is a synthetic, index-based id — it
 * never carries the raw internal phase code, which control-plane views must
 * never expose (ADR-005, UT-051); `label` is already humanized/relabelled.
 */
export interface CockpitTurnCountRow {
  key: string;
  label: string;
  user: number;
  agent: number;
  firstAt: string | null;
  lastAt: string | null;
}

export interface CockpitMetricDetailsView {
  parcels: CockpitMetricParcelView[] | null;
  byModel: CockpitMetricBreakdownRow[];
  byPhase: CockpitMetricBreakdownRow[];
  bySkill: CockpitMetricBreakdownRow[];
  legacy: CockpitMetricLegacyNoticeView | null;
  /** Turn counts by phase/skill (US-012); empty when the snapshot predates
   * `sections.time.value.turnsByPhase`/`turnsBySkill` (2026-09-10). */
  turnsByPhase: CockpitTurnCountRow[];
  turnsBySkill: CockpitTurnCountRow[];
}

export interface CockpitMetricView {
  id: string;
  label: string;
  rawValue: string | number | null;
  displayValue: string;
  unit: string | null;
  status: CockpitSectionStatus;
  coverage: CockpitCoverage;
  provenance: CockpitProvenance[];
  nature: 'reported' | 'calculated' | null;
  explanation: string;
  unavailableReason: string | null;
  relation: string | null;
  technicalValue: string | null;
  primary: boolean;
  updatedAt: string | null;
  sourceCutoffAt: string;
  periodStartedAt: string | null;
  periodEndedAt: string | null;
  provisional: boolean;
  proportion: string | null;
  reasonCode:
    | UsageReason
    | CockpitUsageCostReason
    | 'time-window-unavailable'
    | 'section-load-failed'
    | null;
  impact: string | null;
  action: string | null;
  counts: {
    observed: number;
    reconciled: number;
    pending: number;
    excluded: number;
    exclusionReasons: UsageExclusionReason[];
  } | null;
  stale: boolean;
  usageObservationState: CockpitUsageObservationState | null;
  /** Expandable decomposition (parcels, per-model/phase/skill, legacy
   * notice); `null` when the metric has none (cost cards, old snapshots). */
  details: CockpitMetricDetailsView | null;
}

export interface CockpitControlView extends CockpitViewNode {
  kind: 'control';
  result: CockpitGuaranteeResult;
  resultLabel: string;
  sourceLabel: string;
  required: boolean;
  blocking: boolean;
  evidenceIds: string[];
  observedTotal: number;
  failedTotal: number;
  rationale: string | null;
  expectedAction: string | null;
}

export interface CockpitLimitationView {
  code: string;
  summary: string;
}

export interface CockpitGuaranteeView {
  status: 'complete' | 'incomplete' | 'unavailable';
  statusLabel: string;
  sectionStatus: CockpitSectionStatus;
  coverage: CockpitCoverage;
  reason: string;
  impact: string;
  controls: CockpitControlView[];
  criticalGapIds: string[];
  limitations: CockpitLimitationView[];
}

export interface CockpitSessionCapabilityView {
  capability: SessionObservabilityCapability;
  label: string;
  state: SessionEvidenceState;
  stateLabel: string;
  reasonCode: SessionObservabilityReasonCode | null;
  observedAt: string | null;
}

export interface CockpitSessionJointDiagnosticView {
  summary: string;
  impact: string;
  action: string;
}

export interface CockpitSessionObservabilityView {
  runtimeLabel: string;
  state: SessionIntegrationState;
  stateLabel: string;
  actionCode: SessionObservabilityActionCode;
  actionLabel: string;
  observedAt: string;
  stale: boolean;
  capabilities: CockpitSessionCapabilityView[];
  jointDiagnostic: CockpitSessionJointDiagnosticView | null;
}

/** One AI session binding and the desfecho (outcome) shown for it (ADR-005). */
export interface CockpitSessionOutcomeView {
  bindingId: string;
  runtimeLabel: string;
  issuedAt: string;
  closedAt: string | null;
  usages: number;
  turnsUser: number;
  turnsAgent: number;
  outcomeLabel: string;
  /** Only true for a binding that is genuinely still open (no closedAt and no
   * computed outcome yet); a defensive read, never inferred from status alone. */
  active: boolean;
}

/**
 * One step of the phase stepper (RN-COCK-01). `key` is synthetic and
 * index-based; the internal phase code never reaches a control-plane view
 * (ADR-005, UT-051), only the contract label.
 */
export interface CockpitPhaseStepView {
  key: string;
  label: string;
  state: 'completed' | 'active' | 'pending';
  /** Artifacts observed in the phase, in generation order; empty keeps the step static (RN-COCK-08). */
  artifactIds: string[];
  /**
   * The artifact opened as soon as the phase is selected (RN-COCK-09): the
   * one of the type the pipeline expects for the phase, or the only one there
   * is. `null` when the phase has several artifacts and none of the expected
   * type, so the reader picks from the filtered list instead.
   */
  artifactId: string | null;
}

/** What SCM reported about the delivery (RN-COCK-04); never inferred. */
export interface CockpitDeliveryView {
  status: CockpitDeliveryProjection['status'];
  statusLabel: string;
  /** Sanitized http(s) link, or `null` when the reference is not linkable. */
  pullRequestUrl: string | null;
  /** The reference as reported, shown as text when it cannot be a link. */
  pullRequestReference: string | null;
  commitCount: number;
}

export interface CockpitViewModel {
  activity: { id: string; title: string; status: string };
  header: CockpitHeaderView;
  revision: number | null;
  generatedAt: string;
  sessionObservability: CockpitSessionObservabilityView | null;
  /** Runtime of the observed sessions for the header chip; `null` without one. */
  runtimeLabel: string | null;
  /** `null` when no phase evidence exists, so the stepper is not rendered. */
  phases: CockpitPhaseStepView[] | null;
  /** `null` when nothing deliverable was reported, so the section is not rendered. */
  delivery: CockpitDeliveryView | null;
  guarantee: CockpitGuaranteeView;
  rail: CockpitRailSegment[];
  skills: CockpitSkillView[];
  artifacts: CockpitArtifactView[];
  metrics: CockpitMetricView[];
  sessions: CockpitSessionOutcomeView[];
  relations: CockpitRelation[];
  sectionState: {
    relations: CockpitSectionViewState;
    artifacts: CockpitSectionViewState;
  };
}

export function allCockpitNodes(model: CockpitViewModel): CockpitViewNode[] {
  return [...model.skills, ...model.artifacts, ...model.guarantee.controls];
}
